import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import FolderPermissionBlock from '../components/FolderPermissionBlock';
import { mockAdminMaterials, mockAdminElearning } from '@/mocks/adminData';
import type { AdminMaterial } from '@/mocks/adminData';
import { getAgent, updateAgent, type UserInfo } from '@/api/agent';
import { listMaterial, saveConfidentialAgents, type MaterialItem } from '@/api/materialInfo';
import { getTree, type CategoryNode } from '@/api/materialCategory';

/** 文件类型对应缩略图占位图标 */
const typeThumbnailConfig: Record<string, { icon: string; color: string; bg: string }> = {
  Video: { icon: 'ri-video-line',        color: '#6B7280', bg: '#F3F4F6' },
  PDF:   { icon: 'ri-file-pdf-2-line',   color: '#DC2626', bg: '#FEF2F2' },
  PPT:   { icon: 'ri-file-ppt-2-line',   color: '#D04423', bg: '#FFF0ED' },
  Excel: { icon: 'ri-file-excel-2-line', color: '#1E7E45', bg: '#EDFAF2' },
  Word:  { icon: 'ri-file-word-2-line',  color: '#1A5CA8', bg: '#EDF3FC' },
};

/** 供创建页面共享资料数据（接口未实现，暂用 mock） */
export function getMaterialsStore(): AdminMaterial[] {
  return [...mockAdminMaterials, ...mockAdminElearning];
}

// —— 状态显示逻辑 ——
type DisplayState = 0 | 2 | 3;
function getDisplayState(user: UserInfo): DisplayState {
  if (user.state === 2) return 2;
  if (user.expirydate && user.expirydate < Date.now()) return 3;
  return 0;
}

const statusLabel: Record<number, string> = { 0: '启用中', 2: '已禁用', 3: '已过期' };
const statusColor: Record<number, string> = {
  0: 'text-green-700 bg-green-50 border-green-200',
  2: 'text-gray-500 bg-gray-100 border-gray-200',
  3: 'text-red-600 bg-red-50 border-red-200',
};

function formatDate(timestamp?: number): string {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateTime(timestamp?: number): string {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

const regions = [
  'Europe', 'North America', 'South America', 'Middle East',
  'Southeast Asia', 'South Asia', 'East Asia', 'Africa', 'Oceania',
];

export default function AgentEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // —— 数据加载 ——
  const [agent, setAgent] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // —— Toast ——
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // —— 加载代理商数据 ——
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getAgent(id)
      .then((data) => setAgent(data))
      .catch((e: unknown) => setLoadError((e as Error).message || '加载失败'))
      .finally(() => setLoading(false));
  }, [id]);

  // —— 表单状态 ——
  const [form, setForm] = useState({
    name: '',
    company: '',
    region: '',
    state: 0 as number,
    notes: '',
  });
  const [renewDate, setRenewDate] = useState('');
  const [saved, setSaved] = useState(false);

  // 以下面板保留 UI，数据仅存在于本地状态（后端接口暂不支持）
  const [editModels, setEditModels] = useState<string[] | undefined>(undefined);

  // 分类树（用于 category ID → 名称查找）
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);

  // 机密资料：从后端加载
  const [confMaterials, setConfMaterials] = useState<MaterialItem[]>([]);
  const [confMaterialsLoading, setConfMaterialsLoading] = useState(true);
  const [confGrants, setConfGrants] = useState<Record<string, boolean>>({});
  const [savingConf, setSavingConf] = useState(false);

  // 记录加载失败的缩略图 ID
  const [brokenThumbnails, setBrokenThumbnails] = useState<Set<string>>(new Set());
  const handleThumbnailError = (id: string) => {
    setBrokenThumbnails((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // 表单同步 agent 数据
  useEffect(() => {
    if (!agent) return;
    const ds = getDisplayState(agent);
    setForm({
      name: agent.name ?? '',
      company: agent.company ?? '',
      region: agent.region ?? '',
      state: ds,
      notes: agent.notes ?? '',
    });
    // 同步资料分类权限
    if (agent.modelIds && agent.modelIds.length > 0) {
      setEditModels(agent.modelIds);
    }
  }, [agent]);

  // 加载分类树
  useEffect(() => {
    getTree().then(setCategoryTree).catch(() => {});
  }, []);


  // 加载机密资料列表
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await listMaterial({ confidential: 1, pageSize: 999 });
        if (!cancelled) {
          setConfMaterials(result.list);
          setConfMaterialsLoading(false);
        }
      } catch {
        if (!cancelled) setConfMaterialsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 初始化机密资料授权勾选（agent 数据和 confMaterials 都准备好后）
  useEffect(() => {
    if (!id || confMaterials.length === 0) return;
    const init: Record<string, boolean> = {};
    confMaterials.forEach((m) => {
      init[m.id] = (m.confidentialAgents ?? []).includes(id);
    });
    setConfGrants(init);
  }, [id, confMaterials]);

  // 从分类树计算所有叶子节点数
  const totalLeafCount = useMemo(() => {
    let count = 0;
    for (const primary of categoryTree) {
      for (const sub of primary.subCategories) {
        if (sub.models.length > 0) {
          count += sub.models.length;
        } else {
          count += 1;
        }
      }
    }
    return count;
  }, [categoryTree]);

  // —— 加载中 ——
  if (loading) {
    return (
      <AdminLayout title="代理商编辑" subtitle="加载中...">
        <div className="flex flex-col items-center justify-center py-32 text-gray-400">
          <i className="ri-loader-4-line text-4xl mb-3 animate-spin"></i>
          <p className="text-base font-semibold">正在加载代理商数据...</p>
        </div>
      </AdminLayout>
    );
  }

  // —— 加载失败 ——
  if (loadError || !agent) {
    return (
      <AdminLayout title="代理商编辑" subtitle="加载失败">
        <div className="flex flex-col items-center justify-center py-32 text-gray-400">
          <i className="ri-user-search-line text-4xl mb-3"></i>
          <p className="text-base font-semibold text-red-500 mb-1">{loadError || '未找到该代理商'}</p>
          <button onClick={() => navigate('/admin/agents')} className="mt-4 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">返回列表</button>
        </div>
      </AdminLayout>
    );
  }

  const ds = getDisplayState(agent);
  const isAllModels = !editModels || editModels.length === 0;
  const grantedConfCount = Object.values(confGrants).filter(Boolean).length;

  // 从分类树构建查找映射
  const subLabelMap = new Map<string, string>();
  const subToPrimary = new Map<string, { id: string; label: string; icon: string; color: string }>();
  const modelLabelMap = new Map<string, string>();
  for (const primary of categoryTree) {
    for (const sub of primary.subCategories) {
      subLabelMap.set(sub.id, sub.label);
      subToPrimary.set(sub.id, { id: primary.id, label: primary.label, icon: primary.icon, color: primary.color });
      for (const model of sub.models) {
        modelLabelMap.set(model.id, model.label);
      }
    }
  }

  // —— 保存 ——
  const handleSave = async () => {
    const newExpirydate = renewDate ? new Date(renewDate).getTime() : undefined;

    try {
      await updateAgent({
        id: agent.id,
        name: form.name,
        company: form.company,
        region: form.region,
        expirydate: newExpirydate,
        notes: form.notes,
        state: form.state === 2 ? 2 : 0,
        modelIds: !editModels || editModels.length === 0 ? '' : editModels.join(','),
      });
    } catch (e: unknown) {
      showToast((e as Error).message || '保存失败', 'error');
      return;
    }

    // 保存机密资料授权：仅变更当前代理商的授权，不影响其他代理商
    setSavingConf(true);
    try {
      for (const mat of confMaterials) {
        const wasGranted = (mat.confidentialAgents ?? []).includes(agent.id);
        const isGranted = confGrants[mat.id] ?? false;
        if (wasGranted === isGranted) continue; // 未变更，跳过

        const currentAgents = mat.confidentialAgents ?? [];
        const updatedAgents = isGranted
          ? [...currentAgents, agent.id]
          : currentAgents.filter((a) => a !== agent.id);
        await saveConfidentialAgents(mat.id, updatedAgents);
      }
    } catch (e: unknown) {
      showToast((e as Error).message || '保存机密授权失败', 'error');
      setSavingConf(false);
      return;
    }
    setSavingConf(false);

    showToast('保存成功');
    setSaved(true);
    setTimeout(() => { setSaved(false); navigate('/admin/agents'); }, 1200);
  };

  return (
    <AdminLayout
      title={`编辑代理商 — ${agent.name}`}
      subtitle={`${agent.company ?? '—'} · ${agent.region ?? '—'}`}
    >
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-[99999] flex items-center gap-2 px-4 py-3 bg-white border rounded-xl text-sm font-semibold ${toast.type === 'error' ? 'border-red-200 text-red-700' : 'border-green-200 text-green-700'}`}
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
        >
          <i className={`${toast.type === 'error' ? 'ri-error-warning-fill text-red-500' : 'ri-checkbox-circle-fill text-green-500'} text-base`}></i>
          {toast.message}
        </div>
      )}

      {/* Back + Save bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/admin/agents')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base"></i>
          返回代理商列表
        </button>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="flex items-center gap-1.5 text-sm font-semibold text-green-600">
              <i className="ri-checkbox-circle-line"></i>已保存，正在跳转…
            </span>
          )}
          <button
            onClick={() => navigate('/admin/agents')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={savingConf}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#0069BD' }}
          >
            {savingConf ? '保存中...' : '保存修改'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left: Agent info card */}
        <div className="col-span-1 space-y-4">
          {/* Profile card */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-lg font-bold shrink-0" style={{ background: '#0069BD' }}>
                {(agent.name || '?')[0]}
              </div>
              <div>
                <p className="text-base font-bold text-gray-900">{agent.name}</p>
                <p className="text-sm text-gray-400">{agent.email ?? agent.loginname}</p>
                <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${statusColor[ds]}`}>
                  {statusLabel[ds]}
                </span>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-building-line text-xs w-4 text-center"></i>
                <span>{agent.company || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-map-pin-line text-xs w-4 text-center"></i>
                <span>{agent.region || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-calendar-line text-xs w-4 text-center"></i>
                <span>有效至 {formatDate(agent.expirydate)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-time-line text-xs w-4 text-center"></i>
                <span>加入 {formatDate(agent.createdate)}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">活动数据</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-gray-800">{(agent.totalviews ?? 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-center gap-1"><i className="ri-eye-line text-xs"></i>浏览量</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-gray-800">{(agent.totaldownloads ?? 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-center gap-1"><i className="ri-download-line text-xs"></i>下载量</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">最后活跃：<span className="text-gray-600 font-medium">{formatDateTime(agent.lastlogindate)}</span></p>
            </div>
          </div>
        </div>

        {/* Right: Edit form */}
        <div className="col-span-2 space-y-5">

          {/* Basic info */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <i className="ri-user-line text-gray-400"></i>基本信息
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
                  <i className="ri-lock-line text-xs"></i>登录名
                  <span className="text-gray-300 font-normal normal-case tracking-normal">（不可修改）</span>
                </label>
                <div className="w-full px-3 py-2.5 border border-gray-100 rounded-lg text-sm text-gray-400 bg-gray-50 select-all">{agent.loginname}</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">姓名 *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none"
                  onFocus={(e) => { e.target.style.borderColor = '#0069BD'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">公司 *</label>
                  <input
                    type="text"
                    required
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none"
                    onFocus={(e) => { e.target.style.borderColor = '#0069BD'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">地区 *</label>
                  <div className="relative">
                    <select
                      required
                      value={form.region}
                      onChange={(e) => setForm({ ...form, region: e.target.value })}
                      className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none bg-white cursor-pointer"
                    >
                      <option value="">选择地区</option>
                      {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">账号状态</label>
                <div className="relative">
                  <select
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: Number(e.target.value) })}
                    className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none bg-white cursor-pointer"
                  >
                    <option value={0}>启用中</option>
                    <option value={2}>已禁用</option>
                    <option value={3}>已过期</option>
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Expiry */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <i className="ri-calendar-check-line text-gray-400"></i>账号有效期
            </h3>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100 shrink-0">
                <i className="ri-calendar-line text-sm text-gray-400"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">当前有效期至</p>
                <p className="text-sm font-semibold text-gray-700">{formatDate(agent.expirydate)}</p>
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">续期至</label>
              <input
                type="date"
                value={renewDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setRenewDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none bg-white cursor-pointer"
              />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-2">快捷选择</p>
              <div className="flex gap-2 flex-wrap">
                {[{ label: '+3 个月', months: 3 }, { label: '+6 个月', months: 6 }, { label: '+1 年', months: 12 }, { label: '+2 年', months: 24 }].map(({ label, months }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      const base = agent.expirydate && new Date(agent.expirydate).getTime() > Date.now()
                        ? new Date(agent.expirydate) : new Date();
                      base.setMonth(base.getMonth() + months);
                      setRenewDate(base.toISOString().slice(0, 10));
                    }}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Folder permissions */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <i className="ri-layout-grid-line text-gray-400"></i>资料分类权限（按叶子分类）
              </h3>
              <span className="text-xs text-gray-400">
                {isAllModels
                  ? ''
                  : `已限定 ${editModels?.length ?? 0} / ${totalLeafCount} 个分类`}
              </span>
            </div>
            <FolderPermissionBlock
              categoryTree={categoryTree}
              allowedModels={editModels}
              onChange={setEditModels}
            />
          </div>

          {/* Confidential materials */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <i className="ri-shield-keyhole-line text-gray-400"></i>机密资料授权
              </h3>
              <span className="text-xs text-gray-400">已授权 {grantedConfCount} / {confMaterials.length} 份</span>
            </div>
            {confMaterialsLoading ? (
              <div className="py-8 text-center text-gray-400">
                <i className="ri-loader-4-line text-2xl mb-2 block animate-spin"></i>
                <p className="text-sm">加载机密资料...</p>
              </div>
            ) : confMaterials.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-4 text-center">暂无机密资料</p>
            ) : (
              <div className="space-y-2">
                {confMaterials.map((mat) => {
                  const granted = confGrants[mat.id] ?? false;
                  return (
                    <label
                      key={mat.id}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${granted ? 'border-blue-200 bg-blue-50/30' : 'border-gray-100 bg-gray-50/50 hover:bg-gray-100/60'}`}
                    >
                      <input
                        type="checkbox"
                        checked={granted}
                        onChange={() => setConfGrants((prev) => ({ ...prev, [mat.id]: !prev[mat.id] }))}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        style={{ accentColor: '#0069BD' }}
                      />
                      <div className="w-12 h-8 rounded overflow-hidden bg-gray-100 shrink-0 relative">
                        {(() => {
                          const showPlaceholder = !mat.thumbnail || brokenThumbnails.has(mat.id);
                          if (showPlaceholder) {
                            const cfg = typeThumbnailConfig[mat.type] ?? typeThumbnailConfig.PDF;
                            return (
                              <div className="w-full h-full flex items-center justify-center" style={{ background: cfg.bg }}>
                                <i className={cfg.icon} style={{ color: cfg.color, fontSize: '16px' }}></i>
                              </div>
                            );
                          }
                          return (
                            <img
                              src={mat.thumbnail}
                              alt={mat.title}
                              className="w-full h-full object-cover"
                              onError={() => handleThumbnailError(mat.id)}
                            />
                          );
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{mat.title}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-400">
                          {(() => {
                            const primaryInfo = subToPrimary.get(mat.category);
                            const subLabel = subLabelMap.get(mat.category) || mat.category;
                            const modelLabel = mat.modelCategory ? (modelLabelMap.get(mat.modelCategory) || mat.modelCategory) : null;
                            const parts = [
                              primaryInfo ? { label: primaryInfo.label, icon: primaryInfo.icon } : null,
                              { label: subLabel, icon: null },
                              modelLabel ? { label: modelLabel, icon: null } : null,
                            ].filter(Boolean) as { label: string; icon: string | null }[];
                            return parts.map((part, i) => (
                              <span key={i} className="flex items-center gap-1">
                                {i > 0 && <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>}
                                {part.icon && <i className={`${part.icon} text-gray-400 text-xs`}></i>}
                                <span className={i === parts.length - 1 ? 'text-gray-600 font-medium' : 'text-gray-400'}>{part.label}</span>
                              </span>
                            ));
                          })()}
                          <span className="ml-1 px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-medium">{mat.type}</span>
                        </div>
                      </div>
                      {granted && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 shrink-0 whitespace-nowrap">已授权</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <i className="ri-sticky-note-line text-gray-400"></i>备注
            </h3>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="填写备注，例如：跟进事项、特殊说明、合作背景等（仅管理员可见）"
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">{form.notes.length}/500</p>
          </div>

          {/* Bottom save */}
          <div className="flex gap-3 pb-6">
            <button
              onClick={() => navigate('/admin/agents')}
              className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={savingConf}
              className="flex-1 py-3 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: '#0069BD' }}
            >
              {savingConf ? '保存中...' : '保存修改'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
